/**
 * Вкладка «Героини»: портрет, фото-референс для генерации, замена фото,
 * массовое удаление роликов.
 *
 * Красная строка «фото нет» — не украшение: без фото i2v не работает
 * вовсе (Musebox генерит ролик ИЗ фото), и владелец должен узнать об этом
 * здесь, а не из отказа заказа на вкладке «Уровни».
 */

import { useEffect, useState } from 'react'
import { request } from '../../api/client.js'
import type { HeroineRow, PurgeHeroineResult } from '../../api/types.js'
import { Card, Notice } from '../../components/ui.js'

export interface HeroinesProps {
  heroines: HeroineRow[]
  error: string | null
  onChanged: () => void
}

export function Heroines({ heroines, error, onChanged }: HeroinesProps): JSX.Element {
  return (
    <div className="stack">
      {error !== null && <Notice tone="error">{error}</Notice>}
      {heroines.length === 0 && error === null && (
        <Card>
          <p className="muted">Студийных героинь нет: ассистенток игроки создают сами, со своим фото.</p>
        </Card>
      )}

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--gap)' }}>
        {heroines.map((h) => (
          <HeroineCard key={h.id} heroine={h} onChanged={onChanged} />
        ))}
      </div>
    </div>
  )
}

function HeroineCard({ heroine, onChanged }: { heroine: HeroineRow; onChanged: () => void }): JSX.Element {
  return (
    <Card title={heroine.name} hint={heroine.archetype}>
      <div className="stack">
        <Frame url={heroine.portraitUrl} alt={heroine.name} placeholder="портрета нет" />

        <div className="stack stack--sm">
          <span className="field__label">Фото для генерации (i2v)</span>
          {/*
           * Правда — `photoUrl` (реальный первый файл пака на зоне), не
           * `photoPackUrl` (голая колонка): та переживает удаление пака
           * (`purgeHeroine` с `photos: true` её нарочно не обнуляет) и
           * в этом единственном поддерживаемом сценарии — «снёс фото,
           * надо загрузить новое» — молча соврала бы «фото есть» (раунд
           * правок 1, находка Important №3).
           */}
          {heroine.photoUrl !== null ? (
            <Frame url={heroine.photoUrl} alt={`референс для генерации: ${heroine.name}`} placeholder="" />
          ) : (
            <Notice tone="error">фото нет: заказать ролик нельзя</Notice>
          )}
          {heroine.photoPackUrl !== null && (
            <span className="mono muted" style={{ fontSize: 11 }}>
              папка: {heroine.photoPackUrl}
            </span>
          )}
        </div>

        <PhotoUploadButton heroineId={heroine.id} hasPhoto={heroine.photoUrl !== null} onDone={onChanged} />
        <PurgeAllButton heroineId={heroine.id} onDone={onChanged} />
      </div>
    </Card>
  )
}

/**
 * Картинка по подписанной ссылке или пунктирная рамка-заглушка.
 *
 * `onError` тут не перестраховка: ссылка подписана на 15 минут (правило
 * №5), и вкладка, открытая дольше, получит от зоны отказ вместо картинки.
 * Без обработчика это ровно тот же пустой прямоугольник, что и «фото
 * нет», — владелец решит, что фото пропало, и загрузит его заново поверх
 * живого. Поэтому пишем причину и что сделать.
 */
function Frame({ url, alt, placeholder }: { url: string | null; alt: string; placeholder: string }): JSX.Element {
  const [broken, setBroken] = useState(false)

  // Новая ссылка — новая попытка: без сброса кадр, один раз не открывшийся,
  // остался бы надписью навсегда, даже после загрузки нового фото.
  useEffect(() => setBroken(false), [url])

  if (url !== null && !broken) {
    return (
      <img
        src={url}
        alt={alt}
        onError={() => setBroken(true)}
        style={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
      />
    )
  }
  return (
    <div
      className="muted"
      style={{
        width: '100%',
        aspectRatio: '3 / 4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '0 var(--gap)',
        fontSize: 12,
        border: '1px dashed var(--border)',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      {broken ? 'картинка не открылась — ссылка живёт 15 минут, обнови страницу' : placeholder}
    </div>
  )
}

/**
 * Загрузка фото — `<input type=file>` внутри `<label>` со стилем кнопки:
 * своей кнопки для открытия системного диалога нет, а обычный `<input>`
 * выглядит чужеродно рядом с остальной админкой.
 */
function PhotoUploadButton({
  heroineId,
  hasPhoto,
  onDone,
}: {
  heroineId: string
  hasPhoto: boolean
  onDone: () => void
}): JSX.Element {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const upload = async (file: File): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('photo', file)
      await request(`/admin/heroines/${encodeURIComponent(heroineId)}/photo`, { method: 'POST', body: form })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="stack stack--sm">
      <label className="btn btn--sm" style={{ textAlign: 'center', cursor: busy ? 'default' : 'pointer' }}>
        {busy ? 'Загружаю…' : hasPhoto ? 'Заменить фото' : 'Загрузить фото'}
        <input
          type="file"
          accept="image/jpeg,image/png"
          disabled={busy}
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0]
            // Сброс value — иначе повторный выбор ТОГО ЖЕ файла не породит
            // события change, и вторая попытка после отказа не запустится.
            e.target.value = ''
            if (file !== undefined) void upload(file)
          }}
        />
      </label>
      {error !== null && <Notice tone="error">{error}</Notice>}
    </div>
  )
}

/**
 * Массовое удаление роликов героини — подтверждение вводом id, как
 * `AnonymizeBlock` в `PlayerCard.tsx`: действие необратимо (ролики сняты
 * партнёром за кредиты), и диалог с одной кнопкой закрывают не глядя.
 * Фото-пак не трогает: без него следующий заказ ролика невозможен вовсе.
 */
function PurgeAllButton({ heroineId, onDone }: { heroineId: string; onDone: () => void }): JSX.Element {
  const [open, setOpen] = useState(false)
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<PurgeHeroineResult | null>(null)

  const submit = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      const res = await request<PurgeHeroineResult>(`/admin/heroines/${encodeURIComponent(heroineId)}/media`, {
        method: 'DELETE',
      })
      setResult(res)
      setOpen(false)
      setConfirm('')
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <div className="stack stack--sm">
        <button
          className="btn btn--danger btn--sm"
          type="button"
          onClick={() => {
            setOpen(true)
            setResult(null)
          }}
        >
          Удалить все ролики
        </button>
        {result !== null && result.skipped.length === 0 && (
          <Notice tone="ok">Удалено роликов: {result.clips}.</Notice>
        )}
        {result !== null && result.skipped.length > 0 && (
          /*
           * Раунд правок 1 (находка Important №2): владелец-непрограммист
           * не может ничего сделать с числом «не снеслось файлов: 3» —
           * CLI (`pnpm media:purge --all`) ради этого печатает каждый путь
           * с причиной (см. `purgeHeroine`, `media/purge.ts`, комментарий
           * про `skipped`), и админка обязана показать то же самое, а не
           * зелёную плашку поверх недоделанной работы.
           */
          <Notice tone="warn">
            <div>
              Удалено роликов: {result.clips}. Не снеслось файлов на зоне — {result.skipped.length}, их придётся
              убрать руками:
            </div>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {result.skipped.map((s) => (
                <li key={s.path} className="mono" style={{ fontSize: 12 }}>
                  {s.path} — {s.reason}
                </li>
              ))}
            </ul>
          </Notice>
        )}
      </div>
    )
  }

  return (
    <div
      className="stack stack--sm"
      style={{ padding: 'var(--gap)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-2)' }}
    >
      <p className="card__hint" style={{ margin: 0 }}>
        Сотрёт ВСЕ ролики героини — заказывать их заново придётся за кредиты партнёра. Фото-пак
        останется на месте. Введите <span className="mono">{heroineId}</span>, чтобы подтвердить.
      </p>
      <input
        className="input"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder={heroineId}
        aria-label="Подтверждение"
        disabled={busy}
      />
      {error !== null && <Notice tone="error">{error}</Notice>}
      <div className="row">
        <button
          className="btn btn--danger btn--sm"
          type="button"
          disabled={busy || confirm.trim() !== heroineId}
          onClick={() => void submit()}
        >
          {busy ? 'Удаляю…' : 'Да, удалить всё'}
        </button>
        <button
          className="btn btn--ghost btn--sm"
          type="button"
          disabled={busy}
          onClick={() => {
            setOpen(false)
            setConfirm('')
          }}
        >
          Отмена
        </button>
      </div>
    </div>
  )
}
